import { SelectField, TextField } from "@/components/tool-kit/primitives";
import { SELECT } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  GAS_PRESETS,
  type GasConditionsInput,
  molarVolume,
  PRESSURE_UNITS,
  TEMPERATURE_UNITS,
} from "@/lib/tool-kit/chem-reactions/amounts";
import {
  formatFixed,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";

/** English strings for the gas-conditions block. Widgets spread these in. */
export const GAS_CONDITIONS_STRINGS = {
  gasLabel: "Gas conditions",
  gasHint:
    "Used to turn litres of gas into moles and back, with V = RT/P. Only species written with (g) get a volume.",
  presetStp: "0 °C and 100 kPa — IUPAC STP ({volume} L/mol)",
  presetStpAtm: "0 °C and 1 atm — older STP ({volume} L/mol)",
  presetSatp: "25 °C and 100 kPa — SATP ({volume} L/mol)",
  presetRoomAtm: "25 °C and 1 atm ({volume} L/mol)",
  presetCustom: "Custom temperature and pressure",
  temperatureLabel: "Temperature",
  temperatureUnitLabel: "Temperature unit",
  pressureLabel: "Pressure",
  pressureUnitLabel: "Pressure unit",
  badConditions:
    "Enter a temperature above absolute zero and a positive pressure.",
} as const;

type GasStrings = Record<keyof typeof GAS_CONDITIONS_STRINGS, string>;

const PRESET_KEYS: Record<string, keyof GasStrings> = {
  stp: "presetStp",
  stpAtm: "presetStpAtm",
  satp: "presetSatp",
  roomAtm: "presetRoomAtm",
};

export function GasConditionsFields({
  id,
  strings,
  value,
  onChange,
  fmt,
}: {
  id: string;
  strings: GasStrings;
  value: GasConditionsInput;
  onChange: (value: GasConditionsInput) => void;
  fmt: NumberFormatter;
}) {
  const options = [
    ...GAS_PRESETS.map((preset) => {
      const volume = molarVolume(preset.kelvin, preset.kilopascals) ?? 0;
      return {
        value: preset.id,
        label: t(strings[PRESET_KEYS[preset.id]], {
          volume: formatFixed(fmt, volume, 3),
        }),
      };
    }),
    { value: "custom", label: strings.presetCustom },
  ];
  const set = (patch: Partial<GasConditionsInput>) =>
    onChange({ ...value, ...patch });
  return (
    <div className="space-y-4">
      <SelectField
        hint={strings.gasHint}
        id={`${id}-preset`}
        label={strings.gasLabel}
        onChange={(preset) => set({ preset })}
        options={options}
        value={value.preset}
      />
      {value.preset === "custom" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-end gap-2">
            <div className="grow">
              <TextField
                id={`${id}-temperature`}
                label={strings.temperatureLabel}
                numeric
                onChange={(temperature) => set({ temperature })}
                value={value.temperature}
              />
            </div>
            <select
              aria-label={strings.temperatureUnitLabel}
              className={`${SELECT} w-24 shrink-0`}
              onChange={(event) => set({ temperatureUnit: event.target.value })}
              value={value.temperatureUnit}
            >
              {TEMPERATURE_UNITS.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.symbol}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <div className="grow">
              <TextField
                id={`${id}-pressure`}
                label={strings.pressureLabel}
                numeric
                onChange={(pressure) => set({ pressure })}
                value={value.pressure}
              />
            </div>
            <select
              aria-label={strings.pressureUnitLabel}
              className={`${SELECT} w-24 shrink-0`}
              onChange={(event) => set({ pressureUnit: event.target.value })}
              value={value.pressureUnit}
            >
              {PRESSURE_UNITS.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.symbol}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}
    </div>
  );
}
